import PaginationControls from '@/components/pagination/PaginationControls'
import {
    getFinancialTopRelations,
    RELATION_LABELS,
    TopPartner,
    TopRelationsQueryParams,
} from '@/lib/api/financial/financial-dashboard-api'
import { useCallback, useEffect, useState } from 'react'
import { MdClose } from 'react-icons/md'

const PAGE_SIZE = 20

interface TopPartnersModalProps {
    isOpen: boolean
    onClose: () => void
    title: string
    subtitle?: string
    accentColor: string
    /** Recorte da lista: o MESMO do card que abriu o modal. */
    query: Omit<TopRelationsQueryParams, 'page' | 'pageSize'>
}

const formatCurrency = (cents: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(cents / 100)

/**
 * A lista completa por trás de um card de top.
 *
 * O card mostra os cinco primeiros e não diz quantos ficaram de fora; aqui a
 * paginação vem do banco (`COUNT(*) OVER()`), então o operador vê o total real e
 * chega ao último colocado sem exportar nada.
 *
 * O recorte é herdado do card — período, tipo e status de pagamento —, senão as
 * duas telas mostrariam populações diferentes sem avisar qual é qual.
 */
export default function TopPartnersModal({
    isOpen,
    onClose,
    title,
    subtitle,
    accentColor,
    query,
}: TopPartnersModalProps) {
    const [items, setItems] = useState<TopPartner[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const queryKey = JSON.stringify(query)

    // Trocar o recorte volta para a primeira página: manter a página 7 de uma
    // lista que agora tem duas seria uma tela vazia sem explicação.
    useEffect(() => {
        setPage(1)
    }, [queryKey, isOpen])

    const load = useCallback(async () => {
        if (!isOpen) return
        setLoading(true)
        setError(null)
        try {
            const response = await getFinancialTopRelations({
                ...query,
                page,
                pageSize: PAGE_SIZE,
            })
            if (response.success && response.result) {
                setItems(response.result.items)
                setTotal(response.result.total)
            } else {
                setItems([])
                setTotal(0)
                setError(response.error || 'Erro ao carregar a listagem')
            }
        } catch {
            setItems([])
            setTotal(0)
            setError('Erro ao carregar a listagem')
        } finally {
            setLoading(false)
        }
        // `queryKey` no lugar de `query`: o objeto é recriado a cada render da
        // página e dispararia uma busca por render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, queryKey, page])

    useEffect(() => {
        load()
    }, [load])

    if (!isOpen) return null

    const firstIndex = (page - 1) * PAGE_SIZE

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-3xl">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-base-content">
                            {title}
                        </h3>
                        {subtitle && (
                            <p className="text-sm text-base-content/60">{subtitle}</p>
                        )}
                        <p className="text-xs text-base-content/50 mt-1">
                            {total.toLocaleString('pt-BR')}{' '}
                            {total === 1 ? 'registro' : 'registros'} no período
                        </p>
                    </div>
                    <button
                        className="btn btn-sm btn-ghost btn-circle"
                        onClick={onClose}
                        aria-label="Fechar"
                    >
                        <MdClose className="text-lg" />
                    </button>
                </div>

                {error ? (
                    <div className="flex flex-col items-center justify-center h-40 gap-3">
                        <p className="text-sm text-error">{error}</p>
                        <button className="btn btn-sm btn-secondary" onClick={load}>
                            Tentar novamente
                        </button>
                    </div>
                ) : loading && items.length === 0 ? (
                    <div className="flex items-center justify-center h-40">
                        <span className="loading loading-spinner loading-lg text-secondary" />
                    </div>
                ) : items.length === 0 ? (
                    <div className="flex items-center justify-center h-40 text-sm text-base-content/50">
                        Sem registros no período.
                    </div>
                ) : (
                    <div
                        className={`overflow-x-auto transition-opacity ${
                            loading ? 'pointer-events-none opacity-50' : ''
                        }`}
                    >
                        <table className="table table-sm">
                            <thead>
                                <tr>
                                    <th className="w-12">#</th>
                                    <th>Nome</th>
                                    <th className="text-right">Lançamentos</th>
                                    <th className="text-right">Total</th>
                                    <th className="text-right">Pago</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, idx) => (
                                    <tr key={`${item.relation}-${item.id}`}>
                                        <td>
                                            <span
                                                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                                                style={{ backgroundColor: accentColor }}
                                            >
                                                {firstIndex + idx + 1}
                                            </span>
                                        </td>
                                        <td>
                                            <p className="font-medium">{item.name}</p>
                                            <p className="text-xs text-base-content/60">
                                                {RELATION_LABELS[item.relation]}
                                            </p>
                                        </td>
                                        <td className="text-right">{item.count}</td>
                                        <td className="text-right font-semibold">
                                            {formatCurrency(item.totalAmount)}
                                        </td>
                                        <td className="text-right text-base-content/70">
                                            {formatCurrency(item.totalPaidAmount)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {total > PAGE_SIZE && (
                    <PaginationControls
                        page={page}
                        limit={PAGE_SIZE}
                        total={total}
                        loading={loading}
                        onPageChange={setPage}
                        label="registro"
                        className="mt-4"
                    />
                )}

                <div className="modal-action">
                    <button className="btn btn-sm" onClick={onClose}>
                        Fechar
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </div>
    )
}
